/**
 * Agents Service
 *
 * Service for interacting with AI agent endpoints.
 * Handles streaming chat responses and agent operations.
 */

import type { AssessmentResponse, ConversationTurn, EvaluateRequest, StreamEvent } from '@evaltick/shared';
import { BaseAPIClient, type StreamOptions } from './base';

export type { AssessmentResponse, EvaluateRequest, StreamEvent };

// =============================================================================
// TYPES
// =============================================================================

export interface AgentStreamEvent {
  type: 'status' | 'step' | 'chunk' | 'complete' | 'error';
  stepType?: 'processing' | 'tools' | 'model' | 'complete';
  stepNumber?: number;
  totalSteps?: number;
  description?: string;
  data?: unknown;
  status?: string;
  error?: string;
  timestamp: string;
}

export interface AgentChatRequest {
  message: string;
  threadId?: string;
  context?: Record<string, unknown>;
}

export interface AgentThread {
  id: string;
  createdAt: string;
  updatedAt: string;
  title?: string;
  messageCount: number;
}

export interface AgentThreadsResponse {
  threads: AgentThread[];
  total: number;
  page: number;
  limit: number;
}

// =============================================================================
// SERVICE
// =============================================================================

export class AgentService extends BaseAPIClient {
  /**
   * Stream a follow-up chat message to the agent.
   * Both endpoints use the same request shape and emit the same StreamEvent
   * format (chunk / complete / error) — competencyId and questionType are
   * required by the backend validator.
   */
  async streamChat(
    request: EvaluateRequest,
    options: Omit<StreamOptions<StreamEvent>, 'onEvent'> & {
      onEvent: (event: StreamEvent) => void;
    }
  ): Promise<void> {
    return this.stream<StreamEvent>(
      '/agents/chat/stream-nodes',
      request,
      options
    );
  }

  /**
   * List chat threads
   */
  async listThreads(
    page: number = 1,
    limit: number = 20
  ): Promise<AgentThreadsResponse> {
    return this.get<AgentThreadsResponse>(
      `/agents/threads?page=${page}&limit=${limit}`
    );
  }

  /**
   * Create a new chat thread
   */
  async createThread(): Promise<{ id: string }> {
    return this.post<{ id: string }>('/agents/threads', {});
  }

  /**
   * Delete a chat thread
   */
  async deleteThread(threadId: string): Promise<void> {
    return this.delete(`/agents/threads/${threadId}`);
  }

  /**
   * Get thread messages
   */
  async getThreadMessages(
    threadId: string,
    page: number = 1,
    limit: number = 50
  ): Promise<{ messages: unknown[]; total: number }> {
    return this.get(
      `/agents/threads/${threadId}/messages?page=${page}&limit=${limit}`
    );
  }

  /**
   * Stream an evaluation of a candidate answer
   */
  async streamEvaluate(
    request: EvaluateRequest,
    options: Omit<StreamOptions<StreamEvent>, 'onEvent'> & {
      onEvent: (event: StreamEvent) => void;
    }
  ): Promise<void> {
    return this.stream<StreamEvent>('/agents/evaluate', request, options);
  }

  /**
   * Get the assessment response for a completed evaluation thread
   */
  async getAssessmentResponse(threadId: string): Promise<AssessmentResponse> {
    return this.get<AssessmentResponse>(`/agents/response/${threadId}`);
  }

  /** Full transcript for one question — Evaluate plus every Clarify/voice turn since. */
  async getConversation(
    assessmentId: string,
    interviewTemplateItemId: string
  ): Promise<{ turns: ConversationTurn[] }> {
    return this.get<{ turns: ConversationTurn[] }>(
      `/agents/conversation/${assessmentId}/${interviewTemplateItemId}`
    );
  }
}

export default AgentService;
